// The quote for a case: what a family sees after reporting a death, or an
// imminent plan, through the lead form, or when a team member sends them a
// link to it. It arrives the way the policy flow does (a one-time link
// `?t=`, or whatever the device's session cookie is worth), reads the case
// view itself and renders the at-need quote display as it stands, under the
// flow's header. None of the policy flow's application or payment machinery
// applies here, so none of it is mounted.

import { useEffect, useRef, useState, type ReactNode } from "react";

import QuoteView from "components/AtNeedQuote";
import NoticeBanner from "components/NoticeBanner";
import {
  FIRST_CALL_PHONE_HREF,
  FIRST_CALL_PHONE_LABEL,
} from "constants/phones";
import NotFoundView from "pages/SavedQuote/NotFoundView";
import ErrorView from "pages/SavedQuote/ErrorView";

import {
  PROVENANCE_NONE,
  QuoteFlowAnalyticsProvider,
  useQuoteFlowAnalytics,
  type QuoteFlowCredential,
} from "./analytics";
import ArrivalLoadingScreen from "./ArrivalLoadingScreen";
import AuthInterstitial, {
  type AuthInterstitialProps,
} from "./AuthInterstitial";
import { fetchCaseQuoteView, type CaseQuoteViewResponse } from "./api";
import { Frame, Message, useCaseQuoteShell } from "./CaseQuoteFrame";
import {
  hasPackages,
  nextReportKey,
  preplannedNote,
  savedQuoteFromCaseView,
  screenOnDisplay,
  screenReportKey,
} from "./quote-subject";
import { NO_CREDENTIAL_REASON, type AccessOutcome } from "./quote-access";
import { useQuoteAccess } from "./useQuoteAccess";
import { tagQuoteFlowScope } from "./reporting";

type Phase =
  | { kind: "loading" }
  | { kind: "ready"; view: CaseQuoteViewResponse }
  | { kind: "refused"; reason: string }
  | { kind: "not_found" }
  | { kind: "error" };

/** Where a visit came from, as the view answered it; a field the server
 * never sent reports as unknown rather than as no one. */
function answered(value: string | null | undefined): string | null {
  if (value === undefined) return null;
  return value ?? PROVENANCE_NONE;
}

interface CaseQuoteStationProps {
  quoteId: string;
}

/** The URL's one-time link credential, read once at mount. */
function linkTokenAtMount(): string | null {
  return new URLSearchParams(window.location.search).get("t");
}

export default function CaseQuoteStation({ quoteId }: CaseQuoteStationProps) {
  const [linkToken] = useState(linkTokenAtMount);
  const credential: QuoteFlowCredential = linkToken ? "link" : "none";
  return (
    <QuoteFlowAnalyticsProvider
      quoteId={quoteId}
      quoteType="case"
      credential={credential}
    >
      <CaseQuoteContent quoteId={quoteId} linkToken={linkToken} />
    </QuoteFlowAnalyticsProvider>
  );
}

function CaseQuoteContent({
  quoteId,
  linkToken,
}: CaseQuoteStationProps & { linkToken: string | null }) {
  const { track, noteProvenance } = useQuoteFlowAnalytics();
  const shell = useCaseQuoteShell();
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  const showView = (view: CaseQuoteViewResponse) => {
    noteProvenance({
      quoteOrigin: view.quote.origin_kind,
      grantChannel: answered(view.grant_channel),
      grantOrigin: answered(view.grant_origin),
    });
    setPhase(
      view.kind === "case" ? { kind: "ready", view } : { kind: "not_found" },
    );
  };

  const showOutcome = (outcome: AccessOutcome<CaseQuoteViewResponse>) => {
    if (outcome.kind === "granted") {
      showView(outcome.view);
    } else if (outcome.kind === "refused") {
      setPhase({ kind: "refused", reason: outcome.reason });
    } else {
      setPhase({ kind: outcome.kind });
    }
  };

  useEffect(() => {
    tagQuoteFlowScope(quoteId, "case");
  }, [quoteId]);

  const { exchangePending, reentryRefused, signedInAfterReentry } =
    useQuoteAccess({
      quoteId,
      subject: "case",
      linkToken,
      openOnArrival: true,
      fetchView: fetchCaseQuoteView,
      refusalReason: phase.kind === "refused" ? phase.reason : null,
      onOutcome: showOutcome,
      onExchanged: showView,
    });

  const waiting =
    shell === "pending" || phase.kind === "loading" || exchangePending;
  const screen = screenOnDisplay(screenName(phase), waiting);
  const reason = phase.kind === "refused" ? phase.reason : null;
  const reportKey = screenReportKey(screen, reason);
  const lastReportedKey = useRef<string | null>(null);
  useEffect(() => {
    const next = nextReportKey(reportKey, lastReportedKey.current);
    if (next === null) return;
    lastReportedKey.current = next;
    track("quote_flow_screen_viewed", {
      screen,
      ...(reason === null ? {} : { reason }),
    });
    // One report per screen and reason reached, not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportKey]);

  if (waiting) {
    return (
      <Frame shell={shell}>
        <Message shell={shell}>
          <ArrivalLoadingScreen />
        </Message>
      </Frame>
    );
  }

  if (phase.kind === "ready" && hasPackages(phase.view)) {
    const { view } = phase;
    const note = preplannedNote(view);
    return (
      <Frame shell={shell}>
        <div className="flex flex-1 flex-col bg-white">
          {note && (
            <div className="mx-auto max-w-lg px-3 pt-4">
              <NoticeBanner>{note}</NoticeBanner>
            </div>
          )}
          <QuoteView
            data={savedQuoteFromCaseView(view)}
            footerPlacement={shell === "portal" ? "panel" : "viewport"}
          />
        </div>
      </Frame>
    );
  }

  return (
    <Frame shell={shell}>
      <Message shell={shell}>
        {refusal(phase, {
          quoteId,
          linkToken,
          closedAfterProof: reentryRefused,
          onSignedIn: signedInAfterReentry,
        })}
      </Message>
    </Frame>
  );
}

/** The line a case quote's family calls, as in its header. */
const CASE_LINE = {
  href: FIRST_CALL_PHONE_HREF,
  label: FIRST_CALL_PHONE_LABEL,
};

/** What the analytics call the screen: a quote that holds no package is the
 * "unpriced" screen, not the quote. */
function screenName(phase: Phase): string {
  if (phase.kind === "ready") {
    return hasPackages(phase.view) ? "ready" : "unpriced";
  }
  if (phase.kind === "refused" && phase.reason === NO_CREDENTIAL_REASON) {
    return "no_credential";
  }
  return phase.kind;
}

/** A case that cannot be priced right now: it is out of the area, missing a
 * location, or the engine is down. Nothing is known beyond that, so the copy
 * promises only a call. */
function UnpricedView() {
  return (
    <main className="mx-auto w-full max-w-[560px] flex-1 px-5 pb-16 pt-16 text-center">
      <h1 className="font-serif text-3xl text-soil">
        We&rsquo;re not able to show your quote yet
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        A care specialist will be in touch with your pricing. If you&rsquo;d
        rather talk now, call us at{" "}
        <a href={CASE_LINE.href} className="font-bold text-soil">
          {CASE_LINE.label}
        </a>
        .
      </p>
    </main>
  );
}

function refusal(
  phase: Phase,
  reentry: Pick<
    AuthInterstitialProps,
    "quoteId" | "linkToken" | "closedAfterProof" | "onSignedIn"
  >,
): ReactNode {
  if (phase.kind === "ready") return <UnpricedView />;
  if (phase.kind === "refused") {
    return (
      <AuthInterstitial
        {...reentry}
        reason={phase.reason}
        subject="case"
        supportLine={CASE_LINE}
      />
    );
  }
  if (phase.kind === "not_found") return <NotFoundView />;
  return <ErrorView />;
}
